import React, { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleExclamation,
  faDownload,
  faPlus,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import {
  deleteIncomeUnitById,
  resetDeleteIncomeUnitById,
  deleteMultipleIncomeUnitsByIds,
  getFullTenantSummaryBySnapshotId,
  resetGetFullTenantSummaryBySnapshotId,
  upsertBatchIncomeUnits
} from "../../../redux/actions/incomeUnit/incomeUnit";
import {
  getProfileDetails,
  resetGetProfileDetails,
  putCommercialFlag
} from "../../../redux/actions/profile/profileDetails";
import {
  setSaveResultSuccess,
  resetSaving
} from "../../../redux/actions/saveData/saveData";
import { GetFullTenantSummaryState } from "../../../redux/reducers/getIncomeUnit/getFullTenantSummaryReducer";
import { GetElsLookupsState } from "../../../redux/reducers/getLookups/getElsLookupsReducer";
import { ProfileDetailsState } from '../../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import {
  selectDeleteIncomeUnit,
  selectELSLookups,
  selectGetFullTenantSummary,
  selectProfileGetDetails,
  selectProfileGetOverview,
  selectSaveData,
  selectSaveResult
} from "../../../redux/selectors/exportSelectorPaths";
import { BaseAsyncReducerState } from "../../../redux/types";
import { defaultNumberPerPage } from "../../../assets/data/pagination/pagination";
import Asterisk from "../../../components/common/Asterisk/Asterisk";
import Button from "../../../components/common/Button/Button";
import Checkbox from "../../../components/common/Checkbox/Checkbox";
import DataTable from "../../../components/common/DataTable/DataTable";
import DatePicker from "../../../components/common/DatePicker/DatePicker";
import Dropdown, { Option } from "../../../components/common/Dropdown/Dropdown";
import ErrorMessages from "../../../components/common/ErrorMessages/ErrorMessages";
import Input from "../../../components/common/Input/Input";
import ConfirmationDialog from "../../../components/common/Modal/ConfirmationDialog";
import Modal from "../../../components/common/Modal/Modal";
import MultiselectDropdown from "../../../components/common/MultiselectDropdown/MultiselectDropdown";
import NumberInput from "../../../components/common/NumberInput/NumberInput";
import RadioButton from "../../../components/common/RadioGroup/RadioButton";
import ToolTip from "../../../components/common/ToolTip/ToolTip";
import GuidedExperienceWizard from "../../../components/GuidedExperience/GuidedExperienceWizard";
import { WizardExitTrigger, WizardInteractionEventPayload } from "../../../types/GA4Events/WizardEventParams";
import {
  CommercialFlagType,
  initialCommercialFlag,
  keysOfCommercialFlagType
} from "../../../types/profileDetails/CommercialFlagType";
import { pickFromProfileDetailsType } from "../../../types/profileDetails/ProfileDetailsType";
import trackWizardEvent, { createWizardEventPayload } from "../../../utils/GA4EventHelpers/trackWizardEvent";
import IncomeUnitType, { getDefaultUnit } from "../../../types/unit/IncomeUnit";
import { Variable } from "../../../types/unit/Variable";
import {
  exactMatcher,
  FormError,
  getFormErrorMessageByName,
  hasError,
  useFormErrorContext
} from "../../../utils/context/FormErrorContext";
import {
  ColumnInfo,
  FilterColumnInfo,
  SortingColumnInfo
} from "../../../utils/DataTable/ColumnInfo";
import GenericFilter,
{ Filter, filterData, filterOptions } from "../../../utils/DataTable/GenericFilter/GenericFilter";
import GenericSortingTableHeader from "../../../utils/DataTable/GenericSorting/GenericSortingTableHeader";
import { commaSeparate, formatCurrency } from "../../../utils/formatter";
import { defaultCompare, sortData, SortRule } from "../../../utils/sorting";
import { useReadOnly } from "../../../utils/ReadOnlyContext";
import 'react-toastify/dist/ReactToastify.css';
import './Dropdown.css';
import './LeaseInfo.css';
import CommercialReconciliation from "../../../components/CommercialReconciliation/CommercialReconciliation";
import WarningMessages from "../../../components/common/WarningMessages/WarningMessages";
import { useFormWarningContext } from "../../../utils/context/FormWarningContext";
import { downloadCSV, getCSVCellValue } from "../../../utils/csvExport";
import { getColumns, getCSVColumns, getInternalFilteringColumns } from "../../../utils/ commercialUnit";
import { includesAny } from "../../../utils/common";

export default function LeaseInfoInternal(): JSX.Element {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const { t } = useTranslation(["lease_table", "common", "unit", "unit_table"])
  const { clearErrors, errors, removeError, updateErrors } = useFormErrorContext()
  const { warnings, clearWarnings, warningDisplayed, setWarningDisplayed } = useFormWarningContext();
  const { isReadOnly } = useReadOnly()
  const deleteIncomeUnitState: BaseAsyncReducerState = useSelector(selectDeleteIncomeUnit)
  const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups)
  const getFullTenantSummaryState: GetFullTenantSummaryState = useSelector(selectGetFullTenantSummary)
  const profileDetails: ProfileDetailsState = useSelector(selectProfileGetDetails)
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
  const saveData: SaveDataState = useSelector(selectSaveData)
  const saveResultState: SaveResultState = useSelector(selectSaveResult)
  const [getProfileDetailsDispatched, setGetProfileDetailsDispatched] = useState(false)
  const [commercialFlagData, setCommercialFlagData] = useState<CommercialFlagType>({ ...initialCommercialFlag })
  const [initialGetTenantsDispatched, setInitialGetTenantsDispatched] = useState(false)
  const [data, setData] = useState<IncomeUnitType[]>([])
  const [displayedUnits, setDisplayedUnits] = useState<IncomeUnitType[]>([])
  const [refreshDisplayedUnits, setRefreshDisplayedUnits] = useState<boolean>(false)
  const [showDetailsIds, setShowDetailsIds] = useState<number[]>([])
  const [searchInput, setSearchInput] = useState<string>("")
  const [searchFilter, setSearchFilter] = useState<Filter<IncomeUnitType> | null>(null)
  const [filters, setFilters] = useState<Filter<IncomeUnitType>[]>([])
  const [sortRules, setSortRules] = useState<SortRule<IncomeUnitType>[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [numberPerPage, setNumberPerPage] = useState(defaultNumberPerPage)
  const [isErrorVisible, setIsErrorVisible] = useState<(error: FormError) => boolean>(() => (() => { return true }))
  const [customErrorDisplay, setCustomErrorDisplay] = useState<string | JSX.Element>()
  const [refreshErrors, setRefreshErrors] = useState<boolean>(false)
  const [errorousIds, setErrorousIds] = useState<number[]>([])
  const [newUnitCounter, setNewUnitCounter] = useState(-1)
  const [showWizardModal, setShowWizardModal] = useState(() => {
    let wizardNeverShowed = true
    const currentRollNumber = sessionStorage.getItem("rollNumber")
    if (currentRollNumber) {
      const commericalWizardRolls = sessionStorage.getItem("commericalWizard")
      if (commericalWizardRolls) {
        const rolls = commericalWizardRolls.split(",")
        if (rolls.includes(currentRollNumber)) {
          wizardNeverShowed = false
        }
      }
    }
    return wizardNeverShowed
  })
  const [wizardSubmitted, setWizardSubmitted] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationBody, setConfirmationBody] = useState(<></>)
  const [confirmAction, setConfirmAction] = useState(() => (() => { /* */ }))

  const [activeWizardStep, setActiveWizardStep] = useState(0);
  const handleActiveWizardStep = (step: number) => setActiveWizardStep(step);

  const currencySymbol = "$"
  const tenantSummaryType = "1" // PIER2-1317: When UI sends tenantSummaryType = 1 to the backend, both unit type 1 and 2 on the snapshot are returned from backend
  const tenantSummaryTableContainerClassName = "tenant-summary-table-container"
  const subTypeCodeOptions = ((elsLookupsState.dropdowns["unitSubtypeCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
  const leaseTypeCodeOptions = ((elsLookupsState.dropdowns["leaseTypeCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
  const leaseStatusCodeOptions = ((elsLookupsState.dropdowns["leaseStatusCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
  const profileTypeCode = profileOverview.profile.profile_type_code.toUpperCase()
  const campaignStatusCode = sessionStorage.getItem("campaignStatusCode")
  const currentSnapshotId = sessionStorage.getItem('currentSnapshotId') as string
  const propertyCode = sessionStorage.getItem("propertyCode") as string
  const showCommercialQuestion = (["GLF", "LLE", "HTL", "MTL", "RHO", "LTC"].includes(profileTypeCode)
    || (profileTypeCode === "BIP" && ["340", "341", "352", "361", "602", "470", "472", "478"].includes(propertyCode)))
  const internalFilteringColumns = getInternalFilteringColumns(elsLookupsState, displayedUnits) as ColumnInfo[]
  const comparator = (tenentIdA: number, tenentIdB: number) => {
    const successA = !errorousIds.includes(tenentIdA as number)
    const successB = !errorousIds.includes(tenentIdB as number)
    return defaultCompare(successA, successB, "boolean")
  }
  const headerActions = [
    <Button key="header-action-button" onClick={() => showDetails()} className="accordion-button"
      ariaLabel="Toggle all details">
      <FontAwesomeIcon icon={Array.from({ length: Math.min(data.length - (numberPerPage * (currentPage - 1)), numberPerPage) }, (_, i) => i + (numberPerPage * (currentPage - 1))).every(i => showDetailsIds.includes(data[i].tenantId as number)) ? faChevronUp : faChevronDown} />
    </Button>
  ]
  const columns: ColumnInfo[] = getColumns(elsLookupsState, displayedUnits, subTypeCodeOptions, leaseTypeCodeOptions, leaseStatusCodeOptions, comparator, headerActions)
  const csvColumns = getCSVColumns(elsLookupsState) as ColumnInfo[]

  const exportCommercialUnitsAsCSV = () => {
    // Only field-bound columns export; the status column and the trailing headerActions column have no `id`
    let allColumns = internalFilteringColumns.concat(columns).concat(csvColumns)
    allColumns = allColumns.filter((column) => column.id)
    const rollNumber = sessionStorage.getItem("rollNumber") as string
    const selectedYear = sessionStorage.getItem("activeCampaignYear") as string

    downloadCSV(
      `${rollNumber}_${selectedYear}_commercial_units.csv`,
      allColumns.map((column) => typeof column.display === "string" ? column.display : String(column.id)),
      displayedUnits.map((unit) => allColumns.map((column) => getCSVCellValue(unit, column)))
    )
  }

  const filterColumns: FilterColumnInfo[] = internalFilteringColumns.concat(columns.filter((column) => column.filterDisabled !== true && (column.id || column.filterId)))
  const sortingColumns: SortingColumnInfo[] = columns

  const addUnit = () => {
    const newEmptyUnit: IncomeUnitType = {
      ...getDefaultUnit(),
      snapshotId: Number(currentSnapshotId),
      unitTypeCode: '1',
      tenantId: newUnitCounter,
      componentId: generateComponentId(),
    }
    setData([newEmptyUnit, ...data])
    setDisplayedUnits([newEmptyUnit, ...displayedUnits])
    setShowDetailsIds([newUnitCounter, ...showDetailsIds])
    setNewUnitCounter(newUnitCounter - 1)
    setCurrentPage(1)
  }

  const removeViewUnit = useCallback((tenantId: number | number[]) => {
    if (Array.isArray(tenantId)) {
      setData(data.filter(u => !tenantId.includes(u.tenantId as number)))
      setDisplayedUnits(displayedUnits.filter(u => !tenantId.includes(u.tenantId as number)))
      const corespoindingComponentIds = data.filter((unit) => tenantId.includes(unit.tenantId as number)).map((unit) => unit.componentId)
      updateErrors(errors.filter((err) => !corespoindingComponentIds.includes(err.primaryKey.split(".")[0])))
    } else {
      setData(data.filter((u) => u.tenantId !== tenantId))
      setDisplayedUnits(displayedUnits.filter((u) => u.tenantId !== tenantId))
      setShowDetailsIds(showDetailsIds.filter((id) => id !== tenantId))
      const corespoindingComponentId = data.find((unit) => unit.tenantId === tenantId)?.componentId
      updateErrors(errors.filter((err) => !err.primaryKey.startsWith(corespoindingComponentId as string)))
    }
  }, [data, displayedUnits, errors, showDetailsIds, updateErrors])

  const replaceViewUnit = (unit: IncomeUnitType) => {
    const oldUnit = data.find(u => u.tenantId === unit.tenantId)
    if (oldUnit) {
      setData(prevData =>
        prevData.map(u =>
          u.tenantId === unit.tenantId ? unit : u
        )
      )
      setDisplayedUnits(prevData =>
        prevData.map(u =>
          u.tenantId === unit.tenantId ? unit : u
        ))
    }
  }

  const updateUnitValue = (
    unit: IncomeUnitType,
    key: string,
    value: IncomeUnitType[keyof IncomeUnitType]
  ) => {
    const updatedUnit = {
      ...unit,
      [key]: value
    }
    return updatedUnit
  }

  const handleMultiSelect = (updatedSelection: string[], tenantId: number) => {
    const unit = data.find((unit) => unit.tenantId === tenantId)

    const variables: Variable[] = []
    updatedSelection.forEach(v => {
      const description = (elsLookupsState.dropdowns["variablesOptions"] as Option[]).find(o => o.value === v)
      variables.push({ "code": v, "description": description ? description.text : "" })
    })
    if (unit) {
      replaceViewUnit(updateUnitValue(unit, "variables", variables))
    }
  }

  const handleDateChange = (date: Date | null | undefined, name: string) => {
    if (typeof name !== "string") {
      return
    }
    const componentId = name.split(".")[0];
    const unit = data.find((unit) => unit.componentId === componentId)
    const key = name.split(".")[1]
    if (unit) {
      if (date) {
        const dateStr = format(date, "yyyy-MM-dd")
        replaceViewUnit(updateUnitValue(unit, key, dateStr))
      } else {
        replaceViewUnit(updateUnitValue(unit, key, undefined))
      }
      removeError(componentId + "." + key)
    }

  }

  const handleCurrencyChange = (value: number | null, name: string) => {
    const componentId = name.split(".")[0];
    const unit = data.find((unit) => unit.componentId === componentId)
    if (unit) {
      replaceViewUnit(updateUnitValue(unit, name.split(".")[1], value))
    }
  }

  const updateValue = (e: React.ChangeEvent | number | null, tenantId: number, key: string, isNumber?: boolean, _isValue?: boolean) => {
    const oldUnit = data.find((unit) => unit.tenantId === tenantId)
    const componentId = oldUnit?.componentId
    if (oldUnit) {
      let unit = oldUnit

      if (typeof e === "number" || e === null) {
        unit = updateUnitValue(unit, key, e)
      } else {
        const target = e.target as HTMLSelectElement | HTMLInputElement
        const oldValue = unit[key as keyof IncomeUnitType]
        if (target instanceof HTMLInputElement && target.type === "checkbox") {
          unit = updateUnitValue(unit, key, target.checked)
        } else {
          const value = target.value.trim()
          if (isNumber && value !== null && value !== "") {
            let numericValue = Number(value)
            if (isNaN(numericValue)) {
              unit = updateUnitValue(unit, key, null)
              target.value = ""
            } else if (numericValue < 0) {
              numericValue = -1 * numericValue
              unit = updateUnitValue(unit, key, numericValue)
              target.value = String(numericValue)
            } else {
              unit = updateUnitValue(unit, key, numericValue)
            }
          } else {
            unit = updateUnitValue(unit, key, value === "" ? null : value)
          }

          if (key === "leaseStatusCode") {
            if (value === "1") { // "1" is Vacant - change to Vacant
              unit = {
                ...unit,
                leaseTypeCode: "",
                tenantName: "Vacant",
                // startDate: undefined, // PIER2-1260
                negotiatedDate: undefined,
                endDate: undefined, // If Lease Status = Vacant, blank out both Start and End Date,
                includeInsuranceFlag: undefined,
                includeManagementFlag: undefined,
                includeMaintenanceFlag: undefined,
                includeHydroFlag: undefined,
                includeWaterFlag: undefined,
                includeHvacFlag: undefined,
                includePropertyTaxFlag: undefined,
                landLeaseFlag: undefined,
                annualRent: undefined,
                currentBaseRent: undefined,
                propertyTaxRecovery: undefined,
                operatingRecovery: undefined,
                freeRentMonth: undefined,
                tenantImprovement: undefined,
                stepUpDate1: undefined,
                stepUpDate2: undefined,
                stepUpDate3: undefined,
                stepUpDate4: undefined,
                stepUpRate1: undefined,
                stepUpRate2: undefined,
                stepUpRate3: undefined,
                stepUpRate4: undefined,
                analysisFlag: "N",
                analysisReviewedBy: localStorage.getItem("username") ?? "SYSTEM",
                analysisReviewedDate: new Date().toISOString()
              }
              removeError(componentId + ".leaseTypeCode")
              removeError(componentId + ".currentBaseRent")
            } else if (oldValue === "1" && value !== "1") { // change from Vacant to other status
              unit = {
                ...unit,
                "tenantName": undefined,
                "analysisFlag": "X",
                "analysisReviewedBy": localStorage.getItem("username") ?? "SYSTEM",
                "analysisReviewedDate": new Date().toISOString()
              }
            }
          } else if (key === "leaseTypeCode"
            && (value === "1" || (unit["leaseTypeCode"] === "2" && (value === "3" || value === "4")))) {
            unit = {
              ...unit,
              "includeInsuranceFlag": false,
              "includeManagementFlag": false,
              "includeMaintenanceFlag": false,
              "includeHydroFlag": false,
              "includeWaterFlag": false,
              "includeHvacFlag": false,
              "includePropertyTaxFlag": false,
            }
          } else if (key === "leaseTypeCode" && value === "2") {
            unit = {
              ...unit,
              "includeInsuranceFlag": true,
              "includeManagementFlag": true,
              "includeMaintenanceFlag": true,
              "includeHydroFlag": true,
              "includeWaterFlag": true,
              "includeHvacFlag": true,
              "includePropertyTaxFlag": true,
            }
          } else if (key === "analysisFlag") {
            unit = {
              ...unit,
              "analysisFlag": value,
              "analysisReviewedBy": localStorage.getItem("username") ?? "SYSTEM",
              "analysisReviewedDate": new Date().toISOString()
            }
            if (value === "N" || value === "F") {
              updateErrors([...errors.filter((error) => ![componentId + ".startDate", componentId + ".leaseTypeCode", componentId + ".leaseStatusCode", componentId + ".currentBaseRent"].includes(error.primaryKey))])
            } else if (value !== "Y") {
              updateErrors([...errors.filter((error) => !error.primaryKey.includes(componentId as string))])
            }
          } else if (key === "tenancyTypeCode") {
            if (unit.tenancyTypeCode === "800" && (!unit.designTypeCode || unit.designTypeCode === "")) {
              unit = updateUnitValue(unit, "designTypeCode", "20")
              removeError(componentId + ".designTypeCode")
            }
          } else if (key === "unitSubtypeCode") {
            if (value === "102") { // 102 - Billboard
              unit = {
                ...unit,
                "floorLevel": "",
                "netLeasableArea": null
              }
              updateErrors([...errors.filter((error) => ![componentId + ".unitSubtypeCode", componentId + ".floorLevel", componentId + ".netLeasableArea"].includes(error.primaryKey))])
            }
          }
          removeError(componentId + "." + key)
        }
      }
      replaceViewUnit(unit)
    }
  }

  const handleApplyAnalysisToAll = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const analysisValue = e.target.value
    if (analysisValue === "N" || analysisValue === "F") {
      updateErrors([
        ...errors.filter(
          error => !includesAny(error.primaryKey, [".startDate", ".leaseTypeCode", ".leaseStatusCode", ".currentBaseRent"])
        )
      ])
    } else if (analysisValue !== "Y") {
      clearErrors();
      clearWarnings();
    }

    const analysisReviewedBy = localStorage.getItem("username") ?? "SYSTEM"
    const analysisReviewedDate = (new Date().toISOString())
    displayedUnits.forEach((u) => {
      const updatedData = {
        ...u,
        "analysisFlag": analysisValue,
        "analysisReviewedBy": analysisReviewedBy,
        "analysisReviewedDate": analysisReviewedDate,
      }
      replaceViewUnit(updatedData)
    })
    toast.success(`Analysis ${analysisValue} applied to all displayed units`)
  }

  const showDetails = (tenantId?: number) => {
    if (tenantId !== undefined) {
      const newArr = [...showDetailsIds]
      const indexOfTenantId = showDetailsIds.indexOf(tenantId)
      if (indexOfTenantId >= 0) {
        newArr.splice(indexOfTenantId, 1)
      } else {
        newArr.push(tenantId)
      }
      setShowDetailsIds(newArr)
    } else if (Array.from({ length: Math.min(data.length - (numberPerPage * (currentPage - 1)), numberPerPage) }, (_, i) => i + (numberPerPage * (currentPage - 1))).every(i => showDetailsIds.includes(data[i].tenantId as number))) {
      const newArr = [...showDetailsIds.filter((i) => !(Array.from({ length: Math.min(data.length - (numberPerPage * (currentPage - 1)), numberPerPage) }, (_, i) => data[(i + (numberPerPage * (currentPage - 1)))].tenantId).includes(i)))]
      setShowDetailsIds(newArr)
    } else {
      const newArr = [...showDetailsIds]
      for (let index = numberPerPage * (currentPage - 1); index < Math.min(data.length, numberPerPage * currentPage); index++) {
        if (!newArr.includes(data[index].tenantId as number)) {
          newArr.push(data[index].tenantId as number)
        }
      }
      setShowDetailsIds(newArr)
    }
  }

  const onErrorClick = (error: FormError) => {
    if (error.primaryKey) {
      const componentId = error.primaryKey.split(".")[0]
      const tenantId = data.find((unit) => unit.componentId === componentId)?.tenantId
      if (!showDetailsIds.includes(tenantId as number)) {
        setShowDetailsIds([...showDetailsIds, tenantId as number])
      }
    }
  }

  const getDesignTypeOptions = (tenantId: number, tenancyTypeCode: string) => {
    const unit = data.find((unit) => unit.tenantId === tenantId)

    if (unit) {
      const designTypeOptions = [{ value: undefined, text: "" }] as Option[]
      if (tenancyTypeCode !== "" && elsLookupsState.dropdowns) {
        // Bug fix PIER2-1261: Check if unit's tenancy type code is a valid lookup currently
        const getTTObject = (elsLookupsState.dropdowns["tenancyType"] as Record<string, unknown>[])
          .filter(tt => tt.tenancyTypeCode === tenancyTypeCode)

        // If unit's TT is found, get the design type codes for it
        const designTypesForTT: string[] = getTTObject.length > 0 ? getTTObject[0]["unitDesignTypeCode"] as string[] : []

        // Check that design type list is retrieved from ELS before performing operations on it
        if (designTypesForTT) {
          const activeDesignTypesForUnit = (elsLookupsState.dropdowns["designType"] as Record<string, unknown>[])
            .filter((dt) => designTypesForTT.includes(String(dt.unitDesignTypeCode)))

          activeDesignTypesForUnit && activeDesignTypesForUnit.forEach((option) => {
            designTypeOptions.push({ value: option["unitDesignTypeCode"] as string, text: option["unitDesignTypeDescription"] as string })
          })
        }
        if (!designTypesForTT.includes(String(unit.designTypeCode))) {
          unit.designTypeCode = null
        }
      } else {
        unit.designTypeCode = null
      }
      return designTypeOptions
    }
    return []
  }

  const formatOutput = (value: string | number | null | undefined) => {
    if (value == null || value === undefined) {
      return ""
    } else {
      return value.toString()
    }
  }

  const formatIntegerOutput = (value: string | number | null | undefined) => {
    if (value == null || value === undefined || value === "") {
      return ""
    }

    const num = Number(value)
    if (isNaN(num)) {
      return String(value)
    }

    return String(Math.trunc(num))
  }

  const buildStepUps = (tenantData: IncomeUnitType): JSX.Element[] => {
    const stepUpTitles: JSX.Element[] = []
    const stepUpEls: JSX.Element[] = []
    for (let i = 1; i <= 4; i++) {
      stepUpTitles.push(<div className="accordion-flex-cell grid-span-2-column">
        <div className="accordion-flex-cell-header">
          {t('headings.step-up', { ns: "unit" }) + " " + i}
        </div>
      </div>)
      stepUpEls.push(
        <>
          <div className="accordion-flex-cell">
            <div className="accordion-flex-cell-header">
              {t('headings.step-up-date', { ns: "unit" })}
            </div>
            <div className="accordion-flex-cell-input unhide-overflow">
              <DatePicker name={`${tenantData.componentId as string}.stepUpDate${i}`} id={`${tenantData.componentId as string}.stepUpDate${i}`}
                date={tenantData[`stepUpDate${i}` as keyof IncomeUnitType] as IncomeUnitType["stepUpDate1"]}
                onChange={handleDateChange}
                title={t(`headings.step-date${i}-placeholder`)}
                disabled={tenantData.leaseStatusCode === '1'}
                errorMessage={getFormErrorMessageByName(errors, `${tenantData.componentId as string}.stepUpDate${i}`, exactMatcher)}
              />
            </div>
          </div>
          <div className="accordion-flex-cell">
            <div className="accordion-flex-cell-header">
              {t('headings.step-rent', { ns: "unit" })}
            </div>
            <div className="accordion-flex-cell-input">
              <NumberInput
                name={`${tenantData.componentId as string}.stepUpRate${i}`}
                errorMatcher={exactMatcher}
                negatives
                decimalPlaces={2}
                commas
                symbol='$'
                textAlign='right'
                initialValue={tenantData[`stepUpRate${i}` as keyof IncomeUnitType] as IncomeUnitType["stepUpRate1"]}
                onChange={(value: number | null) => handleCurrencyChange(value, `${tenantData.componentId as string}.stepUpRate${i}`)}
                tooltip={t('tooltips.step-rent-tooltip') + "(" + currencySymbol + ")"} />
            </div>
          </div>
        </>
      )
    }
    return stepUpTitles.concat(stepUpEls)
  }

  const buildTableContent = (): JSX.Element => {
    const windowWidth = window.innerWidth

    const getTextWidth = (text: string, font: string) => {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (context) {
        context.font = font || getComputedStyle(document.body).font

        return context.measureText(text).width
      }
      else {
        return 0
      }
    }

    let maxTenantNameWidth = 0
    for (let index = numberPerPage * (currentPage - 1);
      index < Math.min(displayedUnits.length, numberPerPage * currentPage);
      index++) {
      const tenantName = displayedUnits[index]["tenantName"]
      if (tenantName) {
        const tenantNameWidth = getTextWidth(tenantName as string, "")
        maxTenantNameWidth = Math.max(maxTenantNameWidth, tenantNameWidth)
      }
    }

    const getTdClassName = (id: string) => {
      let wrapClass = ""
      if (!["tenantName"].includes(id)) {
        wrapClass = "no-wrap"
      }

      let widthClass = ""
      if (["tenantName"].includes(id)) {
        const minWidthClassName = id + "-min-width"
        const minWidthVarName = "--" + minWidthClassName
        const minWidthStyle = getComputedStyle(document.body).getPropertyValue(minWidthVarName)
        if (minWidthStyle) {
          let minWidthInPix = 0
          const strMinWidth = minWidthStyle.toString()
          let strMinWidthValue = ""
          let minWidthValue = 0
          if (strMinWidth.endsWith("vw")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 2)
            minWidthValue = Number(strMinWidthValue)
            minWidthInPix = Math.ceil(minWidthValue * windowWidth / 100)
          } else if (strMinWidth.endsWith("px")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 2)
            minWidthValue = Number(strMinWidthValue)
            minWidthInPix = Math.ceil(minWidthValue)
          } else if (strMinWidth.endsWith("rem")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 3)
            minWidthValue = Number(strMinWidthValue)
            const fontSizeStyle = getComputedStyle(document.documentElement).getPropertyValue("font-size")
            const iFontSize = Number(fontSizeStyle.substring(0, fontSizeStyle.length - 2))
            minWidthInPix = Math.ceil(minWidthValue * iFontSize)
          } else if (strMinWidth.endsWith("em")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 2)
            minWidthValue = Number(strMinWidthValue)
            const fontSizeStyle = getComputedStyle(document.body).getPropertyValue("font-size")
            const iFontSize = Number(fontSizeStyle.substring(0, fontSizeStyle.length - 2))
            minWidthInPix = Math.ceil(minWidthValue * iFontSize)
          }
          if ((maxTenantNameWidth + 8) < minWidthInPix) {
            widthClass = "no-wrap"
          } else {
            widthClass = minWidthClassName
          }
        }
      }

      const columnClass = id
      const className = [columnClass, wrapClass, widthClass].join(" ").replaceAll("  ", " ").trim()
      return className.trim()
    }

    const getInternalHeading = (text: string) => {
      return text.replaceAll("\n", " ")
    }

    const tableBody: JSX.Element[] = []
    for (let index = numberPerPage * (currentPage - 1);
      index < Math.min(displayedUnits.length, numberPerPage * currentPage);
      index++) {
      tableBody.push(
        <>
          <tr key={`table-row1-${index}`}
            className={(showDetailsIds.includes(displayedUnits[index].tenantId as number) ? "visibility-visible-first" : "")}
            onClick={() => showDetails(displayedUnits[index].tenantId as number)}>
            {
              columns.map((column, ind) => {
                if (column && Object.keys(column).length > 0 && column["id"]) {
                  const id = column.id
                  let dataValue = (displayedUnits[index] as unknown as Record<string, IncomeUnitType[keyof IncomeUnitType]>)[id]
                  switch (id) {
                    case "leaseTypeCode":
                    case "leaseStatusCode":
                    case "unitSubtypeCode":
                      {
                        if (column.dataLkpFunc) {
                          dataValue = column.dataLkpFunc(dataValue as string) as string
                        } else {
                          dataValue = dataValue as string
                        }
                        break
                      }
                    case "netLeasableArea":
                      dataValue = commaSeparate(dataValue as IncomeUnitType["netLeasableArea"])
                      break
                    case "currentBaseRent":
                      dataValue = formatCurrency(dataValue as IncomeUnitType["currentBaseRent"], i18n.language)
                      break
                    case "analysisFlag":
                      dataValue = dataValue as IncomeUnitType["analysisFlag"]
                      break
                    default:
                      break
                  }
                  const formattedOutput = formatOutput(dataValue as string | null | undefined)
                  if (id === "currentBaseRent") {
                    return <td key={id} className={getTdClassName(id)}>
                      <div className="number-input-container">
                        <div className="number-input-wrapper">
                          <div>{currencySymbol}</div>
                          <div className="width-100">{formattedOutput}</div>
                        </div>
                      </div>
                    </td>
                  } else if (id === "analysisFlag") {
                    return <td key={id} className={getTdClassName(id)}>
                      <div aria-label="analysisFlag">{formattedOutput}</div>
                      {/* added invisible analysis fields for unit test purpose only */}
                      <div className="display-none" aria-label="analysisReviewedBy">{displayedUnits[index].analysisReviewedBy}</div>
                      <div className="display-none" aria-label="analysisReviewedDate">{displayedUnits[index].analysisReviewedDate}</div>
                    </td>
                  } else {
                    return <td key={id} className={getTdClassName(id)}>{formattedOutput}</td>
                  }
                } else {
                  return ind === 0 ?
                    <td key="validation-icon">
                      {
                        errorousIds.includes(displayedUnits[index].tenantId as number) ?
                          <ToolTip contents={
                            <FontAwesomeIcon className="red-font validation-icon" icon={faCircleExclamation} />
                          }>
                            {t("tooltips.validation-failed-tooltip")}
                          </ToolTip>
                          :
                          <ToolTip contents={
                            <FontAwesomeIcon className="green-font validation-icon" icon={faCircleCheck} />
                          }>
                            {t("tooltips.validated-tooltip")}
                          </ToolTip>
                      }
                    </td>
                    :
                    <td key="chevron-icon">
                      <FontAwesomeIcon icon={showDetailsIds.includes(displayedUnits[index].tenantId as number) ? faChevronUp : faChevronDown} />
                    </td>
                }
              })
            }
          </tr>
          <tr key={`table-row2-${index}`}
            className={(showDetailsIds.includes(displayedUnits[index].tenantId as number) ? "visibility-visible-second" : "visibility-hidden")}>
            <td colSpan={11} className="accordion-table-cell">
              <div className="accordion-flex-container">
                <div className="accordion-flex-cell internal-header-cell">
                  <div className="accordion-flex-cell-header">{t("internal-headings.sequence-number")}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.buildingIdNumber`, exactMatcher) ? "table-error" : ""}`}>
                    <Input name={`${displayedUnits[index].componentId as string}.buildingIdNumber`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.buildingIdNumber`, exactMatcher)}
                      isError={hasError(errors, `${displayedUnits[index].componentId as string}.buildingIdNumber`)}
                      hideErrorCross
                      id={`${displayedUnits[index].componentId as string}.buildingIdNumber`}
                      type="number"
                      forcedType="integer"
                      positiveOnly
                      value={formatIntegerOutput(displayedUnits[index].buildingIdNumber as string | number | null | undefined)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "buildingIdNumber")}
                      ariaLabel={`buildingIdNumber-${index}`} />
                  </div>
                </div>
                <div className="accordion-flex-cell internal-header-cell">
                  <div className="accordion-flex-cell-header">{t("internal-headings.tenancy-type")}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.tenancyTypeCode`, exactMatcher) ? "table-error" : ""}`}>
                    <Dropdown name={`${displayedUnits[index].componentId as string}.tenancyTypeCode`}
                      options={(([{ value: undefined, text: "" }] as Option[]).concat(elsLookupsState.dropdowns["tenancyTypeCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))}
                      hideErrorCross
                      value={formatOutput(displayedUnits[index].tenancyTypeCode as string | null)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "tenancyTypeCode")}
                      ariaLabel={`tt-${index}`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.tenancyTypeCode`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell internal-header-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{t("internal-headings.design-type")}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.designTypeCode`, exactMatcher) ? "table-error" : ""}`}>
                    <Dropdown name={`${displayedUnits[index].componentId as string}.designTypeCode`}
                      options={(getDesignTypeOptions(displayedUnits[index].tenantId as number, formatOutput(displayedUnits[index].tenancyTypeCode as string | null))).filter(opt => opt && opt.value !== "")}
                      hideErrorCross
                      value={formatOutput(displayedUnits[index].designTypeCode as string | null)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "designTypeCode")}
                      ariaLabel={`dt-${index}`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.designTypeCode`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell internal-header-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{t("internal-headings.analysis")}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.analysisFlag`, exactMatcher) ? "table-error" : ""}`}>
                    <Dropdown name={`${displayedUnits[index].componentId as string}.analysisFlag`}
                      options={elsLookupsState.dropdowns["rentAnalysisOptionsNoDesc"] as Option[]}
                      hideErrorCross
                      value={displayedUnits[index].analysisFlag as string || ""}
                      disabled={isReadOnly || (displayedUnits[index].leaseStatusCode === "1" && displayedUnits[index].analysisFlag === "N")}
                      onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "analysisFlag")}
                      ariaLabel={`analysisFlag-${index}`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.analysisFlag`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell internal-header-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{t("internal-headings.variables")}</div>
                  <div className={`unhide-overflow accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.variables`, exactMatcher) ? "table-error" : ""}`}>
                    <MultiselectDropdown name={`${displayedUnits[index].componentId as string}.variables`}
                      id={`${displayedUnits[index].componentId as string}.variables`}
                      categoryName="variable"
                      options={(elsLookupsState.dropdowns["variablesOptions"] as Option[]).filter(opt => opt && opt.value !== "")}
                      selectedOptions={displayedUnits[index].variables ? displayedUnits[index].variables.map(v => v.code) : []}
                      setSelectedOptions={(selectedOptions) => handleMultiSelect(selectedOptions, displayedUnits[index].tenantId as number)} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.unit-number'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.tenantUnitNumber`, exactMatcher) ? "table-error" : ""}`}>
                    <Input name={`${displayedUnits[index].componentId as string}.tenantUnitNumber`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.tenantUnitNumber`, exactMatcher)}
                      isError={hasError(errors, `${displayedUnits[index].componentId as string}.tenantUnitNumber`)}
                      hideErrorCross
                      id={`${displayedUnits[index].componentId as string}.tenantUnitNumber`}
                      type="text"
                      value={formatOutput(displayedUnits[index].tenantUnitNumber as string | number | null | undefined)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "tenantUnitNumber")}
                      ariaLabel={`tenantUnitNumber-${index}`} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.tenant-name'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.tenantName`, exactMatcher) ? "table-error" : ""}`}>
                    <Input name={`${displayedUnits[index].componentId as string}.tenantName`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.tenantName`, exactMatcher)}
                      isError={hasError(errors, `${displayedUnits[index].componentId as string}.tenantName`)}
                      hideErrorCross
                      id={`${displayedUnits[index].componentId as string}.tenantName`}
                      type="text"
                      value={formatOutput(displayedUnits[index].tenantName as string | null | undefined)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "tenantName")}
                      ariaLabel={`tenantName-${index}`}
                      disabled={isReadOnly || displayedUnits[index].leaseStatusCode === "1"} />
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.floor-level'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.floorLevel`, exactMatcher) ? "table-error" : ""}`}>
                    <Dropdown name={`${displayedUnits[index].componentId as string}.floorLevel`}
                      options={(([{ value: undefined, text: "" }] as Option[]).concat(elsLookupsState.dropdowns["floorLevelOptions"] as Option[]).filter(opt => opt && opt.value !== ""))}
                      hideErrorCross
                      value={displayedUnits[index].unitSubtypeCode === "102" ? "" : formatOutput(displayedUnits[index].floorLevel as string | null)}
                      readOnly={isReadOnly || (displayedUnits[index].unitSubtypeCode === "102")}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "floorLevel")}
                      ariaLabel={`floor-level-${index}`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.floorLevel`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.leased-area'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.netLeasableArea`, exactMatcher) ? "table-error" : ""}`}>
                    <NumberInput name={`${displayedUnits[index].componentId as string}.netLeasableArea`}
                      commas negatives={false} decimals={false} decimalPlaces={0}
                      initialValue={displayedUnits[index].unitSubtypeCode === "102" ? null : formatOutput(displayedUnits[index].netLeasableArea as string | null | undefined)}
                      readOnly={isReadOnly || (displayedUnits[index].unitSubtypeCode === "102")}
                      onChange={(value: number | null) => updateValue(value, displayedUnits[index].tenantId as number, "netLeasableArea", true, true)}
                      errorMatcher={exactMatcher}
                      tooltip={t('tooltips.leased-area-tooltip')} />
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.unit-use-internal'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.unitSubtypeCode`, exactMatcher) ? "table-error" : ""}`}>
                    <Dropdown name={`${displayedUnits[index].componentId as string}.unitSubtypeCode`} hideErrorCross
                      options={(([{ value: undefined, text: "" }] as Option[]).concat(elsLookupsState.dropdowns["unitSubtypeCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))}
                      value={formatOutput(displayedUnits[index].unitSubtypeCode as string | null)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "unitSubtypeCode")}
                      ariaLabel={`unit-use-${index}`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.unitSubtypeCode`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.lease-status'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.leaseStatusCode`, exactMatcher) ? "table-error" : ""}`}>
                    <Dropdown name={`${displayedUnits[index].componentId as string}.leaseStatusCode`} hideErrorCross
                      options={(([{ value: undefined, text: "" }] as Option[]).concat(elsLookupsState.dropdowns["leaseStatusCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))}
                      value={formatOutput(displayedUnits[index].leaseStatusCode as string | null)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "leaseStatusCode")}
                      ariaLabel={`lease-status-${index}`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.leaseStatusCode`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.lease-type'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.leaseTypeCode`, exactMatcher) ? "table-error" : ""}`}>
                    <Dropdown name={`${displayedUnits[index].componentId as string}.leaseTypeCode`} hideErrorCross
                      options={(([{ value: undefined, text: "" }] as Option[]).concat(elsLookupsState.dropdowns["leaseTypeCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))}
                      value={formatOutput(displayedUnits[index].leaseTypeCode as string | null)}
                      onChange={e => updateValue(e, displayedUnits[index].tenantId as number, "leaseTypeCode")}
                      ariaLabel={`lease-type-${index}`}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.leaseTypeCode`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-5-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.expenses-header', { ns: "unit" }))}</div>
                  <div className="accordion-flex-cell-input">
                    <div className="flex flex-row justify-content-start align-items-center">
                      <div className={`flex operating-expenses-checkbox-container ${hasError(errors, `${displayedUnits[index].componentId as string}.includeInsuranceFlag`, exactMatcher) ? "table-error" : ""}`}>
                        <Checkbox onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "includeInsuranceFlag")}
                          name={`${displayedUnits[index].componentId as string}.includeInsuranceFlag`}
                          defaultChecked={displayedUnits[index].includeInsuranceFlag !== undefined && displayedUnits[index].includeInsuranceFlag as boolean | undefined}
                          checked={displayedUnits[index].includeInsuranceFlag !== undefined && displayedUnits[index].includeInsuranceFlag as boolean | undefined}
                          disabled={displayedUnits[index].leaseStatusCode === '1' || displayedUnits[index].leaseTypeCode === '1' || displayedUnits[index].leaseTypeCode === '2'}
                        />
                        <div>{t("headings.insurance", { ns: "unit" })}</div>
                      </div>
                      <div className={`flex operating-expenses-checkbox-container ${hasError(errors, `${displayedUnits[index].componentId as string}.includeManagementFlag`, exactMatcher) ? "table-error" : ""}`}>
                        <Checkbox onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "includeManagementFlag")}
                          name={`${displayedUnits[index].componentId as string}.includeManagementFlag`}
                          defaultChecked={displayedUnits[index].includeManagementFlag !== undefined && displayedUnits[index].includeManagementFlag as boolean | undefined}
                          checked={displayedUnits[index].includeManagementFlag !== undefined && displayedUnits[index].includeManagementFlag as boolean | undefined}
                          disabled={displayedUnits[index].leaseStatusCode === '1' || displayedUnits[index].leaseTypeCode === '1' || displayedUnits[index].leaseTypeCode === '2'}
                        />
                        <div>{t("headings.management", { ns: "unit" })}</div>
                      </div>
                      <div className={`flex operating-expenses-checkbox-container ${hasError(errors, `${displayedUnits[index].componentId as string}.includeMaintenanceFlag`, exactMatcher) ? "table-error" : ""}`}>
                        <Checkbox onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "includeMaintenanceFlag")}
                          name={`${displayedUnits[index].componentId as string}.includeMaintenanceFlag`}
                          defaultChecked={displayedUnits[index].includeMaintenanceFlag !== undefined && displayedUnits[index].includeMaintenanceFlag as boolean | undefined}
                          checked={displayedUnits[index].includeMaintenanceFlag !== undefined && displayedUnits[index].includeMaintenanceFlag as boolean | undefined}
                          disabled={displayedUnits[index].leaseStatusCode === '1' || displayedUnits[index].leaseTypeCode === '1' || displayedUnits[index].leaseTypeCode === '2'}
                        />
                        <div>{t("headings.maintenance", { ns: "unit" })}</div>
                      </div>
                      <div className={`flex operating-expenses-checkbox-container ${hasError(errors, `${displayedUnits[index].componentId as string}.includeHydroFlag`, exactMatcher) ? "table-error" : ""}`}>
                        <Checkbox onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "includeHydroFlag")}
                          name={`${displayedUnits[index].componentId as string}.includeHydroFlag`}
                          defaultChecked={displayedUnits[index].includeHydroFlag !== undefined && displayedUnits[index].includeHydroFlag as boolean | undefined}
                          checked={displayedUnits[index].includeHydroFlag !== undefined && displayedUnits[index].includeHydroFlag as boolean | undefined}
                          disabled={displayedUnits[index].leaseStatusCode === '1' || displayedUnits[index].leaseTypeCode === '1' || displayedUnits[index].leaseTypeCode === '2'}
                        />
                        <div>{t("headings.hydro", { ns: "unit" })}</div>
                      </div>
                      <div className={`flex operating-expenses-checkbox-container ${hasError(errors, `${displayedUnits[index].componentId as string}.includeWaterFlag`, exactMatcher) ? "table-error" : ""}`}>
                        <Checkbox onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "includeWaterFlag")}
                          name={`${displayedUnits[index].componentId as string}.includeWaterFlag`}
                          defaultChecked={displayedUnits[index].includeWaterFlag !== undefined && displayedUnits[index].includeWaterFlag as boolean | undefined}
                          checked={displayedUnits[index].includeWaterFlag !== undefined && displayedUnits[index].includeWaterFlag as boolean | undefined}
                          disabled={displayedUnits[index].leaseStatusCode === '1' || displayedUnits[index].leaseTypeCode === '1' || displayedUnits[index].leaseTypeCode === '2'}
                        />
                        <div>{t("headings.water", { ns: "unit" })}</div>
                      </div>
                      <div className={`flex operating-expenses-checkbox-container ${hasError(errors, `${displayedUnits[index].componentId as string}.includeHvacFlag`, exactMatcher) ? "table-error" : ""}`}>
                        <Checkbox onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "includeHvacFlag")}
                          name={`${displayedUnits[index].componentId as string}.includeHvacFlag`}
                          defaultChecked={displayedUnits[index].includeHvacFlag !== undefined && displayedUnits[index].includeHvacFlag as boolean | undefined}
                          checked={displayedUnits[index].includeHvacFlag !== undefined && displayedUnits[index].includeHvacFlag as boolean | undefined}
                          disabled={displayedUnits[index].leaseStatusCode === '1' || displayedUnits[index].leaseTypeCode === '1' || displayedUnits[index].leaseTypeCode === '2'}
                        />
                        <div>{t("headings.hvac", { ns: "unit" })}</div>
                      </div>
                      <div className={`flex operating-expenses-checkbox-container ${hasError(errors, `${displayedUnits[index].componentId as string}.includePropertyTaxFlag`, exactMatcher) ? "table-error" : ""}`}>
                        <Checkbox onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "includePropertyTaxFlag")}
                          name={`${displayedUnits[index].componentId as string}.includePropertyTaxFlag`}
                          defaultChecked={displayedUnits[index].includePropertyTaxFlag !== undefined && displayedUnits[index].includePropertyTaxFlag as boolean | undefined}
                          checked={displayedUnits[index].includePropertyTaxFlag !== undefined && displayedUnits[index].includePropertyTaxFlag as boolean | undefined}
                          disabled={displayedUnits[index].leaseStatusCode === '1' || displayedUnits[index].leaseTypeCode === '1' || displayedUnits[index].leaseTypeCode === '2'}
                        />
                        <div>{t("headings.property-tax", { ns: "unit" })}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.land-lease'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.landLeaseFlag`, exactMatcher) ? "table-error" : ""}`}>
                    <div className="width-100 flex flex-row justify-content-center operating-expenses-checkbox-container">
                      <Checkbox name={`${displayedUnits[index].componentId as string}.landLeaseFlag`}
                        id={`${displayedUnits[index].componentId as string}.landLeaseFlag`}
                        defaultChecked={displayedUnits[index].landLeaseFlag != null && displayedUnits[index].landLeaseFlag === true}
                        checked={displayedUnits[index].landLeaseFlag != null && displayedUnits[index].landLeaseFlag === true}
                        onChange={(e) => updateValue(e, displayedUnits[index].tenantId as number, "landLeaseFlag")}
                        disabled={displayedUnits[index].leaseStatusCode === '1'} />
                    </div>
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.annual-rent', { ns: "unit" }))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.annualRent`, exactMatcher) ? "table-error" : ""}`}>
                    <NumberInput name={`${displayedUnits[index].componentId as string}.annualRent`}
                      errorMatcher={exactMatcher}
                      commas negatives decimalPlaces={2}
                      symbol='$'
                      textAlign='right'
                      initialValue={displayedUnits[index].annualRent as IncomeUnitType["annualRent"]}
                      onChange={(value: number | null) => handleCurrencyChange(value, `${displayedUnits[index].componentId as string}.annualRent`)}
                      tooltip={t('tooltips.annual-rent-tooltip')} />
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.current-base-rent'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.currentBaseRent`, exactMatcher) ? "table-error" : ""}`}>
                    <NumberInput name={`${displayedUnits[index].componentId as string}.currentBaseRent`}
                      commas negatives decimalPlaces={2}
                      symbol='$'
                      textAlign='right'
                      initialValue={displayedUnits[index].currentBaseRent as IncomeUnitType["currentBaseRent"]}
                      onChange={(value: number | null) => handleCurrencyChange(value, `${displayedUnits[index].componentId as string}.currentBaseRent`)}
                      tooltip={t('tooltips.current-base-rent-tooltip')}
                      errorMatcher={exactMatcher} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.prop-tax-recoveries', { ns: "unit" }))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.propertyTaxRecovery`, exactMatcher) ? "table-error" : ""}`}>
                    <NumberInput name={`${displayedUnits[index].componentId as string}.propertyTaxRecovery`}
                      errorMatcher={exactMatcher}
                      commas negatives decimalPlaces={2}
                      symbol='$'
                      textAlign='right'
                      initialValue={displayedUnits[index].propertyTaxRecovery as IncomeUnitType["propertyTaxRecovery"]}
                      onChange={(value: number | null) => handleCurrencyChange(value, `${displayedUnits[index].componentId as string}.propertyTaxRecovery`)}
                      tooltip={t('tooltips.prop-tax-recoveries-tooltip')} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.operating-recoveries', { ns: "unit" }))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.operatingRecovery`, exactMatcher) ? "table-error" : ""}`}>
                    <NumberInput name={`${displayedUnits[index].componentId as string}.operatingRecovery`}
                      errorMatcher={exactMatcher}
                      commas negatives decimalPlaces={2}
                      symbol='$'
                      textAlign='right'
                      initialValue={displayedUnits[index].operatingRecovery as IncomeUnitType["operatingRecovery"]}
                      onChange={(value: number | null) => handleCurrencyChange(value, `${displayedUnits[index].componentId as string}.operatingRecovery`)}
                      tooltip={t('tooltips.operating-recoveries-tooltip')} />
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.tenant-improvements', { ns: "unit" }))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.tenantImprovement`, exactMatcher) ? "table-error" : ""}`}>
                    <NumberInput name={`${displayedUnits[index].componentId as string}.tenantImprovement`}
                      errorMatcher={exactMatcher}
                      commas negatives decimalPlaces={2}
                      symbol='$'
                      textAlign='right'
                      initialValue={displayedUnits[index].tenantImprovement as IncomeUnitType["tenantImprovement"]}
                      onChange={(value: number | null) => handleCurrencyChange(value, `${displayedUnits[index].componentId as string}.tenantImprovement`)}
                      tooltip={t('tooltips.tenant-improvements-tooltip')} />
                  </div>
                </div>
                <div className="accordion-flex-cell">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.rent-free-months', { ns: "unit" }))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.freeRentMonth`, exactMatcher) ? "table-error" : ""}`}>
                    <NumberInput name={`${displayedUnits[index].componentId as string}.freeRentMonth`}
                      errorMatcher={exactMatcher}
                      commas negatives
                      initialValue={displayedUnits[index].freeRentMonth as IncomeUnitType["freeRentMonth"]}
                      onChange={(value: number | null) => updateValue(value, displayedUnits[index].tenantId as number, "freeRentMonth", true, true)}
                      decimals={false}
                      tooltip={t('tooltips.rent-free-months-tooltip')} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-3-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.negotiated-date'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.negotiatedDate`, exactMatcher) ? "table-error unhide-overflow" : "unhide-overflow"}`}>
                    <DatePicker name={`${displayedUnits[index].componentId as string}.negotiatedDate`}
                      id={`${displayedUnits[index].componentId as string}.negotiatedDate`}
                      date={displayedUnits[index].negotiatedDate as string | undefined}
                      onChange={handleDateChange}
                      title={t('tooltips.negotiated-date-tooltip')}
                      disabled={displayedUnits[index].leaseStatusCode === '1'}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.negotiatedDate`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-3-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.start-date'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.startDate`, exactMatcher) ? "table-error unhide-overflow" : "unhide-overflow"}`}>
                    <DatePicker name={`${displayedUnits[index].componentId as string}.startDate`}
                      id={`${displayedUnits[index].componentId as string}.startDate`}
                      date={displayedUnits[index].startDate as string | undefined}
                      onChange={handleDateChange}
                      title={t('tooltips.start-date-tooltip')}
                      maxDate={parseISO(new Date().getFullYear() + "-12-31")}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.startDate`, exactMatcher)} />
                  </div>
                </div>
                <div className="accordion-flex-cell grid-span-2-column">
                  <div className="accordion-flex-cell-header">{getInternalHeading(t('headings.end-date'))}</div>
                  <div className={`accordion-flex-cell-input ${hasError(errors, `${displayedUnits[index].componentId as string}.endDate`, exactMatcher) ? "table-error unhide-overflow" : "unhide-overflow"}`}>
                    <DatePicker name={`${displayedUnits[index].componentId as string}.endDate`}
                      id={`${displayedUnits[index].componentId as string}.endDate`}
                      date={displayedUnits[index].endDate as string | undefined}
                      onChange={handleDateChange}
                      title={t('tooltips.end-date-tooltip')}
                      minDate={parseISO("2019-06-01")}
                      disabled={displayedUnits[index].leaseStatusCode === '1'}
                      errorMessage={getFormErrorMessageByName(errors, `${displayedUnits[index].componentId as string}.endDate`, exactMatcher)} />
                  </div>
                </div>
                {
                  buildStepUps(displayedUnits[index])
                }
                {!isReadOnly &&
                  <div className="accordion-flex-row grid-span-8-column flex flex-row-reverse">
                    <Button
                      type="dangerV2"
                      onClick={() => {
                        if (!isReadOnly) {
                          confirmRemoveUnit(String(displayedUnits[index].snapshotId), displayedUnits[index].tenantId as number)
                        }
                      }}
                      text={t("delete", { ns: "common" }).toLocaleUpperCase()}
                      ariaLabel={t("remove") + " " + index}
                      className="accordion-delete-btn"
                    />
                  </div>
                }
              </div>
            </td>
          </tr>
          <tr key={`table-row3-${index}`}
            className={("visibility-hidden" +
              (showDetailsIds.includes(displayedUnits[index].tenantId as number) ?
                " visibility-visible-third"
                :
                " visibility-visible-third-hidden"))}>
            <td colSpan={11}>
              <div></div>
            </td>
          </tr>
        </>
      )
    }
    return (<>{tableBody}</>)
  }

  const confirmRemoveUnit = (snapshotId: string, tenantId: number) => {
    const onConfirmRemoveAction = () => {
      setShowConfirmationModal(false)
      if (tenantId >= 0) {
        dispatch(resetDeleteIncomeUnitById())
        dispatch(deleteIncomeUnitById(snapshotId, tenantId.toString()))
      } else {
        removeViewUnit(tenantId)
      }
    }
    setConfirmAction(() => onConfirmRemoveAction)
    setConfirmationBody(<>{t("confirm-remove-unit", { ns: "common" })}</>)
    setShowConfirmationModal(true)
  }

  const confirmOpenModal = () => {
    const onConfirmOpenModalAction = () => {
      setShowWizardModal(true)
      const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
        wizard_type: "commercial",
        wizard_interaction_type: "enter",
        wizard_entry_trigger: "wizard_button_click"
      })
      trackWizardEvent(wizardEventPayload)
      setShowConfirmationModal(false)
    }
    setConfirmAction(() => onConfirmOpenModalAction)
    setConfirmationBody(<>
      <Trans t={t}
        i18nKey="confirm-open-wizard"
        ns="common"
        components={{ p: <p /> }}
      />
    </>)
    setShowConfirmationModal(true)
  }

  const confirmExitWizardModal = (active_step: number, exit_method: WizardExitTrigger, submitted = false) => {
    setWizardSubmitted(submitted)
    if (submitted) {
      if (showWizardModal) {
        setShowWizardModal(false)
      }
      return
    }

    const onConfirmExitWizardModalAction = () => {
      const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
        wizard_type: "commercial",
        wizard_interaction_type: "exit",
        wizard_step: active_step,
        wizard_exit_trigger: exit_method,
      })
      trackWizardEvent(wizardEventPayload)
      setShowConfirmationModal(false)
      if (showWizardModal) {
        setShowWizardModal(false)
      }
    }
    setConfirmAction(() => onConfirmExitWizardModalAction)
    setConfirmationBody(<>
      <Trans t={t}
        i18nKey="confirm-exit-wizard"
        ns="common"
        components={{ p: <p /> }}
      />
    </>)
    setShowConfirmationModal(true)
  }

  const selectNoCommercialWithUnits = () => {
    const onConfirmNoCommercialWithUnitsAction = () => {
      setShowConfirmationModal(false)
      setCommercialFlagData({ hasCommercialFlag: false })
    }
    setConfirmAction(() => onConfirmNoCommercialWithUnitsAction)
    setConfirmationBody(<>{t('messages.confirmation-modal')}</>)
    setShowConfirmationModal(true)
  }

  const selectNoCommercialWithoutUnits = () => {
    setCommercialFlagData({ hasCommercialFlag: false })
  }

  const getPagniatedUnits = useCallback(() => {
    const lastIdx = currentPage * numberPerPage
    const firstIdx = lastIdx - numberPerPage
    return displayedUnits.slice(firstIdx, lastIdx)
  }, [currentPage, displayedUnits, numberPerPage])


  const getDisplayedUnitsAnalysisValue = () => {
    let value = ""
    if (displayedUnits && displayedUnits.length > 0) {
      const analysisSet: Set<string> = new Set(displayedUnits.map(u => u.analysisFlag))
      if (analysisSet.size === 1) {
        value = [...analysisSet][0]
      }
    }
    return value
  }

  const generateComponentId = () => {
    // Temporary local fallback for browsers/environments without crypto.randomUUID.
    const random = typeof globalThis.crypto?.randomUUID === "function"
      ? String(globalThis.crypto.randomUUID())
      : `${Date.now()}.${Math.random().toString(36).slice(2)}`
    const parts = random.split(".")
    const componentId = parts[parts.length - 1]
    return componentId
  }

  // Used to identify which unpersisted units have spectral errors
  const addComponentIdToUnits = useCallback((units: IncomeUnitType[]): IncomeUnitType[] => {
    return units.map(unit => {
      if (unit.componentId) {
        return unit
      }

      let componentId = null
      if (unit.tenantId) {
        componentId = String(unit.tenantId)
      } else {
        componentId = generateComponentId()
      }
      unit["componentId"] = componentId
      return unit
    })
  }, [])

  useEffect(() => {
    clearErrors();
    clearWarnings();
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (searchInput) {
      setSearchFilter({
        column: "tenantName",
        filterColumn: filterColumns.find(c => c.id === "tenantName") as FilterColumnInfo,
        value: searchInput,
        method: filterOptions["string"][0],
      })
    } else {
      setSearchFilter(null)
    }
  }, [filterColumns, searchInput])

  useEffect(() => {
    if (!initialGetTenantsDispatched) {
      dispatch(resetGetFullTenantSummaryBySnapshotId())
      dispatch(getFullTenantSummaryBySnapshotId(currentSnapshotId, tenantSummaryType))
      setInitialGetTenantsDispatched(true)
    }
  }, [currentSnapshotId, dispatch, initialGetTenantsDispatched])

  useEffect(() => {
    if (initialGetTenantsDispatched && !getFullTenantSummaryState.loading) {
      if (getFullTenantSummaryState.status === 200) {
        const units = addComponentIdToUnits(getFullTenantSummaryState.tenantSummary)
        setData(units)
        setShowDetailsIds([])
        setSearchInput("")
        setFilters([])
        setSortRules([])
      } else {
        setInitialGetTenantsDispatched(false)
      }
    }
  }, [getFullTenantSummaryState, initialGetTenantsDispatched, addComponentIdToUnits])

  useEffect(() => {
    if (!getProfileDetailsDispatched) {
      dispatch(resetGetProfileDetails())
      dispatch(getProfileDetails(currentSnapshotId))
      setGetProfileDetailsDispatched(true)
    }
  }, [currentSnapshotId, dispatch, getProfileDetailsDispatched])

  useEffect(() => {
    if (getProfileDetailsDispatched && !profileDetails.loading) {
      if ((profileDetails.status === 200 && profileDetails.snapshotId === currentSnapshotId)) {
        setCommercialFlagData(pickFromProfileDetailsType(profileDetails.data, keysOfCommercialFlagType))
      } else {
        setGetProfileDetailsDispatched(false)
      }
    }
  }, [currentSnapshotId, getProfileDetailsDispatched, profileDetails.data, profileDetails.loading, profileDetails.snapshotId, profileDetails.status])

  useEffect(() => {
    const getFilteredAndSortedData = () => {
      if (data && data.length > 0) {
        const extraFilters = []
        if (searchFilter) {
          extraFilters.push(searchFilter)
        }
        const filteredData = filterData(data, [...extraFilters, ...filters])
        const sorted = sortData<IncomeUnitType>(filteredData, sortRules)
        return sorted
      }
      return []
    }

    if (refreshDisplayedUnits) {
      setRefreshDisplayedUnits(false)
      setDisplayedUnits(getFilteredAndSortedData())
    }
  }, [data, filters, refreshDisplayedUnits, searchFilter, sortRules])

  useEffect(() => {
    const errorTenantIdSet = new Set<number>(errors.map(error => data.find(unit => unit.componentId === error.primaryKey.split(".")[0])?.tenantId as number))
    setErrorousIds([...errorTenantIdSet])
  }, [errors, data])

  useEffect(() => {
    if (refreshErrors) {
      setRefreshErrors(false)

      const isErrorVisible = (error: FormError) => {
        const errorComponentId = error.primaryKey.split(".")[0]
        if (errorComponentId === "additionalInfo") {
          return true
        }
        const pagniatedUnits = getPagniatedUnits()
        const pagniatedComponentIds = pagniatedUnits.map(u => u.componentId as string)
        if (error && error.primaryKey) {
          return pagniatedComponentIds.includes(errorComponentId)
        }
        return true
      }
      if (setIsErrorVisible) {
        setIsErrorVisible(() => isErrorVisible)
      }

      if (errors.length > 0 && setCustomErrorDisplay) {
        let customErrorDisplay = "Total error count: " + errors.length
        if (data.length > displayedUnits.length) {
          const displayedUnitsIds = displayedUnits.map(unit => unit.componentId as string)
          const displayedUnitsErrors = errors.filter((error) => displayedUnitsIds.includes(error.primaryKey.split(".")[0]))
          const displayedUnitsTotalErrorDisplay = "after apply filters: " + displayedUnitsErrors.length
          customErrorDisplay = customErrorDisplay + ", " + displayedUnitsTotalErrorDisplay
        }
        setCustomErrorDisplay(customErrorDisplay)
      }
    }
  }, [displayedUnits, errors, getPagniatedUnits, refreshErrors, setCustomErrorDisplay, setIsErrorVisible, data])

  useEffect(() => {
    if (errors.length > 0) {
      setRefreshErrors(true)
    }
  }, [currentPage, displayedUnits, errors, numberPerPage])

  useEffect(() => {
    if (data && data.length > 0) {
      setRefreshDisplayedUnits(true)
    }
  }, [data, filters, searchFilter, sortRules])

  useEffect(() => {
    const submitCommercial = () => {
      const undefToNull = (unit: IncomeUnitType) => {
        const normalizedUnit = { ...unit } as IncomeUnitType
        for (const [key, value] of Object.entries(normalizedUnit)) {
          if (value === undefined || (key === "floorLevel" && value === "")) {
            (normalizedUnit as unknown as Record<string, unknown>)[key] = null
          }
        }
        return normalizedUnit
      }

      if (isReadOnly || data.length === 0) {
        dispatch(setSaveResultSuccess())
        return
      }

      dispatch(putCommercialFlag(currentSnapshotId, {
        "commercial_flag_data": commercialFlagData,
        "spectral_section": profileTypeCode.toLowerCase() + "-comm-units",
        "property_code": propertyCode,
        "is_internal": true,
        "trigger_spectral": true
      }))

      const added = data.filter((tenant) => tenant.tenantId && tenant.tenantId < 0)
      const updated = data.filter((tenant) => tenant.tenantId && tenant.tenantId >= 0)
      if (added.length > 0 || updated.length > 0) {
        if (showCommercialQuestion && commercialFlagData.hasCommercialFlag === false) {
          if (updated.length > 0) {
            console.log("LeaseInfoInternal delete multiple units because hasCommercialFlag is false")
            dispatch(deleteMultipleIncomeUnitsByIds(currentSnapshotId, updated.map((tenant) => tenant.tenantId) as number[]))
          }
          if (added.length > 0) {
            removeViewUnit(added.map(u => u.tenantId as number))
          }
        } else {
          const normalizedUpdated = updated.map((unit) => undefToNull(unit))
          const normalizedAdded = added.map((unit) => {
            const normalizedUnit = undefToNull(unit)
            normalizedUnit.tenantId = null
            return normalizedUnit
          })
          dispatch(upsertBatchIncomeUnits(currentSnapshotId, {
            "added": normalizedAdded,
            "updated": normalizedUpdated,
            "property_code": propertyCode,
            "profile_type_code": profileTypeCode,
            "is_internal": true,
            "trigger_spectral": true,
            "is_commercial": true,
            "allow_warning_override": warningDisplayed
          }))
        }
      }
    }

    if (saveData.saving) {
      dispatch(resetSaving())
      clearErrors()
      clearWarnings()
      submitCommercial()
    }
  }, [clearErrors, clearWarnings, commercialFlagData, currentSnapshotId, data, dispatch, isReadOnly, profileTypeCode, propertyCode, removeViewUnit, saveData.saving, showCommercialQuestion, warningDisplayed])

  useEffect(() => {
    if (!deleteIncomeUnitState.loading && deleteIncomeUnitState.status) {
      if (deleteIncomeUnitState.status === 200 && "data" in deleteIncomeUnitState && deleteIncomeUnitState.data) {
        if (Array.isArray(deleteIncomeUnitState.data)) {
          const deletedTenantIds: number[] = deleteIncomeUnitState.data as unknown as number[]
          removeViewUnit(deletedTenantIds)
          setSearchInput("")
          setFilters([])
          setSortRules([])
        } else {
          const deletedTenantId = Number(deleteIncomeUnitState.data)
          removeViewUnit(deletedTenantId)
        }
        console.error(deleteIncomeUnitState.message)
      } else {
        console.error(deleteIncomeUnitState.message)
      }
      dispatch(resetDeleteIncomeUnitById())
    }
  }, [deleteIncomeUnitState, dispatch, removeViewUnit, t])

  useEffect(() => {
    const isSavedDataCommercialFlagType = (data: SaveResultState["data"]): data is CommercialFlagType => {
      if (data && typeof data === "object" && "hasCommercialFlag" in data) {
        return true
      }
      return false
    }

    if (saveResultState.status) {
      if (saveResultState.status === 200) {
        if ("data" in saveResultState && saveResultState.data && Array.isArray(saveResultState.data) && saveResultState.url && saveResultState.url?.includes("upsert-units")) {
          setInitialGetTenantsDispatched(false)
          if (wizardSubmitted) { // if wizard submitted and save result success
            setCurrentPage(1)
            setWizardSubmitted(false)
          }
        } else if (isSavedDataCommercialFlagType(saveResultState.data)) {
          setGetProfileDetailsDispatched(false)
        }
      }
      let hasWarnings = false;
      let hasErrors = false;
      if ("result" in saveResultState && saveResultState.result) {
        if ("warnings" in saveResultState.result && saveResultState.result.warnings.length > 0) {
          hasWarnings = true;
        }
        if ("errors" in saveResultState.result && saveResultState.result.errors.length > 0) {
          hasErrors = true;
        }
      }
      if (saveResultState.loadingCount === 0 && (hasWarnings || warnings.length > 0) && !hasErrors && errors.length === 0) {
        setWarningDisplayed(true);
      }
    }
  }, [currentSnapshotId, dispatch, saveResultState, t, wizardSubmitted, warnings, errors, setWarningDisplayed])

  useEffect(() => {
    const summaryTableContainers = document.getElementsByClassName(tenantSummaryTableContainerClassName)
    if (summaryTableContainers && summaryTableContainers.length > 0) {
      const summaryTableContainer = summaryTableContainers[0]
      summaryTableContainer.scrollTo({
        left: 0,
        behavior: 'auto',
      })
    }
  }, [currentPage])

  return (
    <div className="unit-table-container">
      <ErrorMessages errors={errors}
        isErrorVisible={isErrorVisible}
        onErrorClick={onErrorClick}
        customDisplay={customErrorDisplay} />
      <WarningMessages warnings={warnings} />
      <CommercialReconciliation />

      {showCommercialQuestion ?
        <div className="lease-info-commercial">
          <p>{t("commercial") as string} <Asterisk /></p>
          <div className="radio-wrapper">
            <span className="side-by-side">
              <RadioButton isError={hasError(errors, "hasCommercialFlag")} name="commercial-yes" id="commercial-yes"
                text={t("confirm") as string} textPosition="left" value="true"
                ariaLabel="Yes, has commercial units"
                currentValue={commercialFlagData.hasCommercialFlag}
                onChange={() => {
                  setCommercialFlagData({ hasCommercialFlag: true })
                }} />
              <RadioButton isError={hasError(errors, "hasCommercialFlag")} name="commercial-no" id="commercial-no"
                text={t("cancel") as string} textPosition="left" value="false"
                currentValue={commercialFlagData.hasCommercialFlag}
                ariaLabel="No, does not have commercial units"
                onChange={() => {
                  data.length > 0 ?
                    selectNoCommercialWithUnits()
                    :
                    selectNoCommercialWithoutUnits()
                }} />
            </span>
            {hasError(errors, "hasCommercialFlag") ?
              <div className="input-error-message radio-error">
                {getFormErrorMessageByName(errors, "hasCommercialFlag")}
              </div>
              :
              ""
            }
          </div>
        </div> : <></>
      }
      {(commercialFlagData.hasCommercialFlag || (!showCommercialQuestion)) ?
        <div className='unit-table-subcontainer'>
          <div className="unit-table-summary-wrapper">
            <div className="unit-table-header-row">
              <div></div>
              <div className="unit-table-control-group">
                {!isReadOnly && data.length >= 0 && (
                  <ToolTip contents={
                    <Dropdown name="apply-analysis-to-all"
                      className="apply-analysis-to-all-dropdown"
                      options={(elsLookupsState.dropdowns["rentAnalysisOptions"] ?? []) as Option[]}
                      onChange={handleApplyAnalysisToAll}
                      value={getDisplayedUnitsAnalysisValue()}
                      ariaLabel="Apply Analysis to all" />
                  }>
                    {t("tooltips.apply-analysis-to-all-tooltip")}
                  </ToolTip>
                )}
                <ToolTip contents={
                  <Button onClick={exportCommercialUnitsAsCSV}
                    text={<FontAwesomeIcon icon={faDownload} />}
                    ariaLabel="exportCSV"
                    className='internal-header' />
                }>
                  {t("unit_table:unit-table.export-csv-tooltip")}
                </ToolTip>
                {!isReadOnly && <ToolTip contents={
                  <Button onClick={() => confirmOpenModal()}
                    text={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                    ariaLabel="guidedExperienceWizard"
                    disabled={!(getFullTenantSummaryState.loading === false && getFullTenantSummaryState.status === 200)
                      || campaignStatusCode === "EXTERNAL_SUBMITTED" || campaignStatusCode === "INTERNAL_IN_PROGRESS"} />
                }>
                  {t("tooltips.wizard-tooltip")}
                </ToolTip>}
                {!isReadOnly && <ToolTip contents={
                  <Button onClick={() => addUnit()} text={<FontAwesomeIcon icon={faPlus} />} ariaLabel="addUnit" />
                }>
                  {t('tooltips.add-unit-tooltip')}
                </ToolTip>}
                <GenericFilter<IncomeUnitType>
                  columns={filterColumns}
                  filters={filters}
                  setFilters={setFilters} />
                <ToolTip contents={<input name="tenant-search" id="tenant-search" type="search"
                  placeholder={t("search", { ns: "lease_table" })} className="tenant-search-input"
                  onChange={(e) => setSearchInput(e.target.value)} autoComplete="off"
                  value={searchInput} maxLength={250}
                  aria-label={t("search", { ns: "lease_table" })} />}>
                  {t("search", { ns: "lease_table" })}
                </ToolTip>

              </div>
            </div>

            <DataTable thead={<GenericSortingTableHeader columns={sortingColumns}
              sortRules={sortRules} setSortRules={setSortRules} />}
              className="tenant-summary-table internal-table"
              tableContainerClassName={tenantSummaryTableContainerClassName}
              totalPages={Math.ceil((displayedUnits ? displayedUnits.length : data.length) / numberPerPage)}
              currentPage={currentPage}
              changePage={setCurrentPage}
              numberPerPage={numberPerPage}
              changeNumberPerPage={setNumberPerPage}>
              {buildTableContent()}
            </DataTable>

          </div>
        </div> : <></>
      }
      {showWizardModal && !isReadOnly &&
        ((commercialFlagData.hasCommercialFlag || (!showCommercialQuestion)) &&
          campaignStatusCode !== "EXTERNAL_SUBMITTED" && campaignStatusCode !== "INTERNAL_IN_PROGRESS") &&
        <Modal title={t('headings.guided-experience-wizard')}
          onCloseModal={() => confirmExitWizardModal(activeWizardStep, "x_icon", false)}
          class="guided-experience-wizard-modal">
          <GuidedExperienceWizard snapshotId={Number(currentSnapshotId)}
            onCancel={confirmExitWizardModal}
            sendActiveStepToParent={handleActiveWizardStep} />
        </Modal>}
      {showConfirmationModal &&
        <ConfirmationDialog
          onCancel={() => { setShowConfirmationModal(false) }}
          onConfirm={() => confirmAction()}
          headerClass="no-border"
          buttonGroupClass="justify-content-center no-border">
          {confirmationBody}
        </ConfirmationDialog>
      }
    </div>
  )
}
